import { Box, Stack, Typography } from "@mui/material";
import React from "react";
import logoImage from "../assets/mainlogo1.png";

const LoadingPage = ({ title = "Loading this page" }) => {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f8f7f4",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        fontFamily: "'Sora', 'Inter', sans-serif",
      }}
    >
      {/* Background glow 1 — top left violet */}
      <Box
        sx={{
          position: "absolute",
          top: -120,
          left: -120,
          width: 480,
          height: 480,
          borderRadius: "50%",
          background: "rgba(99,60,180,0.09)",
          pointerEvents: "none",
        }}
      />

      {/* Background glow 2 — bottom right */}
      <Box
        sx={{
          position: "absolute",
          bottom: -80,
          right: -80,
          width: 360,
          height: 360,
          borderRadius: "50%",
          background: "rgba(168,85,247,0.06)",
          pointerEvents: "none",
        }}
      />

      {/* Center group */}
      <Box sx={{ textAlign: "center", position: "relative", zIndex: 2 }}>
        {/* Orb — spinner + logo */}
        <Box
          sx={{
            position: "relative",
            width: 160,
            height: 160,
            mx: "auto",
            mb: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Outer spinning arc */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              border: "2.5px solid transparent",
              borderTopColor: "#633cb4",
              borderRightColor: "rgba(99,60,180,0.3)",
              borderBottomColor: "rgba(99,60,180,0.15)",
              borderLeftColor: "transparent",
              animation: "spin 1.8s linear infinite",
              "@keyframes spin": {
                from: { transform: "rotate(0deg)" },
                to: { transform: "rotate(360deg)" },
              },
            }}
          />

          {/* Static outer ring */}
          <Box
            sx={{
              position: "absolute",
              inset: 12,
              borderRadius: "50%",
              border: "1px solid rgba(99,60,180,0.15)",
              backgroundColor: "rgba(99,60,180,0.04)",
              pointerEvents: "none",
            }}
          />

          {/* Logo circle */}
          <Box
            sx={{
              position: "absolute",
              inset: 22,
              borderRadius: "50%",
              backgroundColor: "#ffffff",
              border: "1px solid rgba(99,60,180,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              boxShadow:
                "0 4px 20px rgba(99,60,180,0.18), 0 1px 4px rgba(99,60,180,0.08)",
            }}
          >
            {/* Logo image — swap src to your actual logo */}
            <Box
              component="img"
              src={logoImage}
              alt="LiteUpper"
              sx={{
                width: "65%",
                height: "65%",
                objectFit: "contain",
              }}
            />
          </Box>
        </Box>

        {/* App name */}
        <Typography
          sx={{
            fontSize: "1.35rem",
            fontWeight: 800,
            color: "#1a1a2e",
            letterSpacing: "-0.02em",
            mb: 0.5,
            animation: "fadeUp 0.7s ease both",
            "@keyframes fadeUp": {
              from: { opacity: 0, transform: "translateY(10px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          LiteUpper
        </Typography>

        {/* Loading label */}
        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 600,
            letterSpacing: "0.12em",
            color: "rgba(99,60,180,0.65)",
            textTransform: "uppercase",
            mb: 3,
            animation: "fadeUp 0.7s 0.1s ease both",
          }}
        >
          {title}
        </Typography>

        {/* Scanning bar */}
        <Box
          sx={{
            width: 160,
            height: 2,
            backgroundColor: "rgba(99,60,180,0.08)",
            borderRadius: 2,
            mx: "auto",
            mb: 2.5,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: "100%",
              width: "35%",
              borderRadius: 2,
              background:
                "linear-gradient(90deg, transparent, #633cb4, #a855f7, #633cb4, transparent)",
              animation: "scan 2s ease-in-out infinite",
              "@keyframes scan": {
                "0%": { transform: "translateX(-200%)" },
                "100%": { transform: "translateX(500%)" },
              },
            }}
          />
        </Box>

        {/* Bouncing dots */}
        <Stack direction="row" spacing={1} justifyContent="center">
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: "#633cb4",
                animation: "bounce 1.4s ease-in-out infinite",
                animationDelay: `${i * 0.18}s`,
                "@keyframes bounce": {
                  "0%, 100%": { opacity: 0.25, transform: "scale(0.75)" },
                  "50%": { opacity: 1, transform: "scale(1.15)" },
                },
              }}
            />
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

export default LoadingPage;
